import { memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Icon, Popover, Switch, Tabs, Text, useTheme } from '@Sage/hoi-poi-ui';

import { FLAGSMITH_FLAGS_TO_CONFIG } from 'constants/Features';

import {
    isAutomationNotificationsEnabled,
    setAutomationNotificationsEnabled,
} from './automationNotificationsFlag';
import {
    isEmbedSandboxAvailable,
    isEmbedSandboxEnabled,
    setEmbedSandboxEnabled,
} from './embedSandbox';
import {
    getAvailableDevEnvironments,
    getDevEnvironmentOverride,
    hasDevEnvironmentOverride,
    isDevEnvironmentSwitcherEnabled,
    setDevEnvironment,
} from './environments';
import {
    clearFlagOverrides,
    getFlagOverrides,
    hasFlagOverrides,
    setFlagOverride,
} from './flagsmithOverrides';
import {
    isLocalFormFragmentAvailable,
    isLocalFormFragmentEnabled,
    setLocalFormFragmentEnabled,
} from './localFormFragment';
import { isDiscardedListsEnabled, setDiscardedListsEnabled } from './showDiscardedListsFlag';
import './styles.scss';

const TAB_CUSTOM = 'custom';
const TAB_ENVIRONMENTS = 'environments';
const TAB_FLAGSMITH = 'flagsmith';

const OverrideSparkles = () => (
    <>
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
        <span className="fm-dev__popup__sparkle" />
    </>
);

const TabTitle = memo(({ icon, label }) => (
    <span className="fm-dev__popup__tab-title">
        <Icon name={icon} size="small" />
        {label}
    </span>
));

TabTitle.displayName = 'DevPopUpTabTitle';

const ToolsSwitchRow = memo(({ checked, description, isOverridden, label, onChange }) => (
    <div className={`fm-dev__popup__row${isOverridden ? ' fm-dev__popup__row--override' : ''}`}>
        <div className="fm-dev__popup__row-copy">
            <Text type="caption" bold>
                {label}
            </Text>
            {description ? (
                <Text type="caption" className="fm-dev__popup__row-desc">
                    {description}
                </Text>
            ) : null}
        </div>
        <Switch aria-label={label} size="small" checked={checked} onChange={onChange} />
    </div>
));

ToolsSwitchRow.displayName = 'DevPopUpSwitchRow';

const DevPopUp = memo(() => {
    const theme = useTheme();
    const flagsmith = useSelector((state) => state.config?.flagsmith || {});
    const overrideEnvironment = useMemo(() => getDevEnvironmentOverride(), []);
    const hasEnvOverride = useMemo(() => hasDevEnvironmentOverride(), []);
    const flagOverrides = useMemo(() => getFlagOverrides(), []);
    const hasFlagsOverride = useMemo(() => hasFlagOverrides(), []);
    const showEmbedSandbox = isEmbedSandboxAvailable();
    const hasEmbedSandboxOverride = useMemo(() => isEmbedSandboxEnabled(), []);
    const showLocalFormFragment = isLocalFormFragmentAvailable();
    const hasLocalFormFragmentOverride = useMemo(() => isLocalFormFragmentEnabled(), []);
    const hasAutomationNotificationsOverride = useMemo(
        () => isAutomationNotificationsEnabled(),
        [],
    );
    const hasDiscardedListsOverride = useMemo(() => isDiscardedListsEnabled(), []);
    const hasAnyOverride =
        hasEnvOverride ||
        hasFlagsOverride ||
        hasEmbedSandboxOverride ||
        hasLocalFormFragmentOverride ||
        hasAutomationNotificationsOverride ||
        hasDiscardedListsOverride;
    const [activeTab, setActiveTab] = useState(TAB_ENVIRONMENTS);

    const setEnvironment = useCallback(
        (name) => {
            // Clicking the active override deselects it and returns to the deploy default
            if (overrideEnvironment && name === overrideEnvironment) {
                setDevEnvironment('');
                window.location.reload();
                return;
            }

            setDevEnvironment(name);
            window.location.reload();
        },
        [overrideEnvironment],
    );

    const handleFlagChange = useCallback((key, checked) => {
        setFlagOverride(key, checked);
        window.location.reload();
    }, []);

    const handleClearFlagOverrides = useCallback(() => {
        clearFlagOverrides();
        window.location.reload();
    }, []);

    const handleEmbedSandboxChange = useCallback((checked) => {
        setEmbedSandboxEnabled(checked);
        window.location.reload();
    }, []);

    const handleLocalFormFragmentChange = useCallback((checked) => {
        setLocalFormFragmentEnabled(checked);
        window.location.reload();
    }, []);

    const handleAutomationNotificationsChange = useCallback((checked) => {
        setAutomationNotificationsEnabled(checked);
        window.location.reload();
    }, []);

    const handleDiscardedListsChange = useCallback((checked) => {
        setDiscardedListsEnabled(checked);
        window.location.reload();
    }, []);

    const toolsTabs = useMemo(() => {
        const tabs = [
            {
                key: TAB_ENVIRONMENTS,
                title: <TabTitle icon="world" label="Environments" />,
                content: (
                    <div className="fm-dev__popup__buttons">
                        {getAvailableDevEnvironments().map((env) => (
                            <Button
                                key={env.name}
                                className="fm-dev__popup__env-btn"
                                size="medium"
                                type={env.name === overrideEnvironment ? 'primary' : 'secondary'}
                                onClick={() => setEnvironment(env.name)}
                            >
                                {env.name}
                            </Button>
                        ))}
                    </div>
                ),
            },
            {
                key: TAB_FLAGSMITH,
                title: <TabTitle icon="flag" label="Flagsmith" />,
                content: (
                    <div className="fm-dev__popup__flags">
                        <div className="fm-dev__popup__flags__list">
                            {FLAGSMITH_FLAGS_TO_CONFIG.map((flag) => (
                                <ToolsSwitchRow
                                    key={flag}
                                    checked={Boolean(flagsmith[flag])}
                                    isOverridden={Object.prototype.hasOwnProperty.call(
                                        flagOverrides,
                                        flag,
                                    )}
                                    label={flag}
                                    onChange={(checked) => handleFlagChange(flag, checked)}
                                />
                            ))}
                        </div>
                        <Button
                            size="small"
                            type="secondary"
                            isDisabled={!hasFlagsOverride}
                            onClick={handleClearFlagOverrides}
                        >
                            Clear overrides
                        </Button>
                    </div>
                ),
            },
        ];

        tabs.push({
            key: TAB_CUSTOM,
            title: <TabTitle icon="sandbox" label="Custom" />,
            content: (
                <div className="fm-dev__popup__flags">
                    <div className="fm-dev__popup__flags__list">
                        <ToolsSwitchRow
                            checked={hasAutomationNotificationsOverride}
                            description="Off by default. Turn on to show the Automations notifications button."
                            isOverridden={hasAutomationNotificationsOverride}
                            label="Automation notifications"
                            onChange={handleAutomationNotificationsChange}
                        />
                        <ToolsSwitchRow
                            checked={hasDiscardedListsOverride}
                            description="Off by default. Turn on to show discarded members in marketing lists."
                            isOverridden={hasDiscardedListsOverride}
                            label="Show discarded lists"
                            onChange={handleDiscardedListsChange}
                        />
                        {showEmbedSandbox ? (
                            <ToolsSwitchRow
                                checked={hasEmbedSandboxOverride}
                                description="Hosts Sales Orders CRUD at /ssm-embed/example.html (same Web4 dev server) and skips POST /api/external/v1/forms."
                                isOverridden={hasEmbedSandboxOverride}
                                label="Sales Orders SDK sandbox"
                                onChange={handleEmbedSandboxChange}
                            />
                        ) : null}
                        {showLocalFormFragment ? (
                            <ToolsSwitchRow
                                checked={hasLocalFormFragmentOverride}
                                description="Loads the Iberfluid form iframe from https://localhost:3010 (npm run start:web4 in iberfluid-form). Trust that HTTPS cert once in the browser."
                                isOverridden={hasLocalFormFragmentOverride}
                                label="Local Iberfluid form"
                                onChange={handleLocalFormFragmentChange}
                            />
                        ) : null}
                    </div>
                </div>
            ),
        });

        return tabs;
    }, [
        flagOverrides,
        flagsmith,
        handleAutomationNotificationsChange,
        handleDiscardedListsChange,
        handleClearFlagOverrides,
        handleEmbedSandboxChange,
        handleLocalFormFragmentChange,
        handleFlagChange,
        hasAutomationNotificationsOverride,
        hasDiscardedListsOverride,
        hasEmbedSandboxOverride,
        hasLocalFormFragmentOverride,
        hasFlagsOverride,
        overrideEnvironment,
        setEnvironment,
        showEmbedSandbox,
        showLocalFormFragment,
    ]);

    const toolsContent = useMemo(
        () => (
            <div className="fm-dev__popup__tools">
                <div className="fm-dev__popup__header">
                    <span className="fm-dev__popup__header-icon">
                        <Icon name="tool" size="medium" color={theme.colors.primary.white} />
                    </span>
                    <div className="fm-dev__popup__header-copy">
                        <Text type="subtitle" color={theme.colors.primary.white}>
                            Dev Tools
                        </Text>
                        <Text type="caption" color={theme.colors.primary.white}>
                            Local overrides for this browser
                        </Text>
                    </div>
                </div>
                <Tabs
                    className="fm-dev__popup__tabs"
                    classes={{
                        content: 'fm-dev__popup__tabs-content',
                        tabPane: 'fm-dev__popup__tab-pane',
                        tabsContainer: 'fm-dev__popup__tabs-shell',
                    }}
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    tabs={toolsTabs}
                />
            </div>
        ),
        [activeTab, theme.colors.primary.white, toolsTabs],
    );

    if (!isDevEnvironmentSwitcherEnabled()) return null;

    return (
        <div className="fm-dev__popup">
            <Popover placement="top" content={toolsContent} overlayInnerStyle={{ padding: 0 }}>
                <div
                    className={`fm-dev__popup__tag${
                        hasAnyOverride ? ' fm-dev__popup__tag--override' : ''
                    }`}
                >
                    {hasAnyOverride && <OverrideSparkles />}
                    <span className="fm-dev__popup__tag-inner">
                        <span className="fm-dev__popup__tag-icon">
                            <Icon name="tool" size="small" color={theme.colors.primary.white} />
                        </span>
                        <Text color={theme.colors.primary.white}>DEV TOOLS</Text>
                    </span>
                </div>
            </Popover>
        </div>
    );
});

DevPopUp.displayName = 'DevPopUp';

export default memo(DevPopUp);
